import React, { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { ChannelActions } from "@Store";
import { useDispatch } from "react-redux";
import { IChannel } from "@Interfaces";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { useTranslation } from "react-i18next";

interface ICloneChannelContent extends IChannel {}

const CloneChannelContent: React.FC<ICloneChannelContent> = (props) => {
    const { t } = useTranslation();

    const [newTitle, setNewTitle] = useState(`${props.title} (Copy)`);
    const [newDescription, setNewDescription] = useState(
        props.description || ""
    );
    const [error, setError] = useState<string>("");
    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                minWidth: "300px",
                pt: 2,
            }}
        >
            <TextField
                label={
                    t("dialogs.clone_channel.new_title") || "New Channel Title"
                }
                value={newTitle}
                onChange={(e) => {
                    setNewTitle(e.target.value);
                    setError("");
                }}
                fullWidth
                required
                error={!!error && error.includes("title")}
                inputProps={{ maxLength: 60 }}
                helperText={`${newTitle.length}/60 characters`}
                variant="outlined"
                id="clone-channel-title"
            />

            <TextField
                label={
                    t("dialogs.clone_channel.new_description") ||
                    "Description (Optional)"
                }
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                fullWidth
                multiline
                rows={3}
                inputProps={{ maxLength: 100 }}
                helperText={`${newDescription.length}/100 characters`}
                variant="outlined"
                id="clone-channel-description"
            />

            {error && (
                <Box
                    sx={{
                        color: "error.main",
                        fontSize: "0.875rem",
                        mt: 1,
                    }}
                >
                    {error}
                </Box>
            )}
        </Box>
    );
};

const CloneChannelActions: React.FC<ICloneChannelContent> = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<any>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const handleClone = async () => {
        const titleInput = document.getElementById(
            "clone-channel-title"
        ) as HTMLInputElement;
        const descriptionInput = document.getElementById(
            "clone-channel-description"
        ) as HTMLInputElement;

        const newTitle = titleInput?.value || "";
        const newDescription = descriptionInput?.value || "";

        if (!newTitle || newTitle.length < 1 || newTitle.length > 60) {
            setError(
                t("dialogs.clone_channel.title_validation_error") ||
                    "Title must be between 1 and 60 characters"
            );
            return;
        }

        setLoading(true);
        setError("");

        try {
            const overrides: any = {};

            if (newTitle && newTitle !== props.title) {
                overrides.title = newTitle;
            }
            if (newDescription && newDescription !== props.description) {
                overrides.description = newDescription;
            }

            dispatch(
                ChannelActions.cloneChannel({
                    group_id: props._id,
                    overrides,
                })
            );

            dispatch(dialogActions.hideDialog());
        } catch (err) {
            setError(
                err?.message ||
                    t("dialogs.clone_channel.error_generic") ||
                    "Failed to clone channel. Please try again."
            );
            setLoading(false);
        }
    };

    return (
        <>
            {error && (
                <div
                    style={{
                        color: "red",
                        fontSize: "0.875rem",
                        marginRight: "auto",
                    }}
                >
                    {error}
                </div>
            )}
            <Button
                onClick={handleClone}
                variant="contained"
                size="small"
                color="primary"
                type="button"
                disabled={loading}
            >
                {loading
                    ? t("common.cloning") || "Cloning..."
                    : t("dialogs.clone_channel.clone_button") ||
                      "Clone Channel"}
            </Button>
        </>
    );
};

export default {
    content: CloneChannelContent,
    actions: CloneChannelActions,
    showHeader: false,
    showActions: true,
    disableCancelButton: false,
};
